/**
 * UserDetailModal - ユーザー詳細情報表示モーダル
 * FL-47: 管理者ユーザが自社のユーザを登録できる機能
 */

import React from 'react';
import { XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CompanyUser } from '../../services/companyUserService';
import { getCompanyRoleLabel, getRoleColor } from '../../utils/roleUtils';
import { isDemoMode } from '../../utils/environment';

interface UserDetailModalProps {
  isOpen: boolean;
  user: CompanyUser | null;
  onClose: () => void;
  onEdit: (user: CompanyUser) => void;
  onDelete: (userId: string) => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  user,
  onClose,
  onEdit,
  onDelete,
}) => {
  const isDemo = isDemoMode();

  if (!isOpen || !user) return null;

  // 日時フォーマット
  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div
        data-testid='user-detail-modal'
        className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto'
      >
        {/* ヘッダー */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>ユーザー詳細</h2>
          <button
            onClick={onClose}
            className='p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
            aria-label='閉じる'
          >
            <XMarkIcon className='h-5 w-5 text-gray-500 dark:text-gray-400' />
          </button>
        </div>

        {/* コンテンツ */}
        <div className='p-6 space-y-8'>
          {/* 基本情報セクション */}
          <div>
            <h3 className='text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4'>
              基本情報
            </h3>
            <div className='grid grid-cols-2 gap-6'>
              {/* ユーザー名 */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  ユーザー名
                </label>
                <p className='text-sm text-gray-900 dark:text-white font-medium'>{user.name}</p>
              </div>

              {/* メールアドレス */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  メールアドレス
                </label>
                <p className='text-sm text-gray-900 dark:text-white break-all'>{user.email}</p>
              </div>

              {/* 権限 */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  権限
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}
                >
                  {getCompanyRoleLabel(user.role)}
                </span>
              </div>

              {/* ステータス */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  ステータス
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.status === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'
                  }`}
                >
                  {user.status === 'active' ? 'アクティブ' : '無効'}
                </span>
              </div>
            </div>
          </div>

          {/* 詳細情報セクション */}
          <div>
            <h3 className='text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4'>
              詳細情報
            </h3>
            <div className='grid grid-cols-2 gap-6'>
              {/* 作成日時 */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  作成日時
                </label>
                <p className='text-sm text-gray-900 dark:text-white'>
                  {formatDateTime(user.createdAt)}
                </p>
              </div>

              {/* 更新日時 */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  更新日時
                </label>
                <p className='text-sm text-gray-900 dark:text-white'>
                  {formatDateTime(user.updatedAt)}
                </p>
              </div>

              {/* 最終ログイン */}
              {user.lastLoginAt && (
                <div>
                  <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                    最終ログイン
                  </label>
                  <p className='text-sm text-gray-900 dark:text-white'>
                    {formatDateTime(user.lastLoginAt)}
                  </p>
                </div>
              )}

              {/* ユーザーID */}
              <div>
                <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                  ユーザーID
                </label>
                <p className='text-sm text-gray-900 dark:text-white font-mono text-xs'>{user.id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* フッター - アクションボタン */}
        <div className='flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700'>
          {/* デモモード時は無効化 */}
          <button
            onClick={() => {
              if (!isDemo) {
                onEdit(user);
              }
            }}
            disabled={isDemo}
            title={isDemo ? 'デモモードでは編集できません' : '編集'}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 border transition-colors ${
              isDemo
                ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed'
                : 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 dark:border-blue-500 dark:focus:ring-offset-gray-800'
            }`}
          >
            <PencilIcon className='h-4 w-4 mr-2' />
            編集
          </button>

          <button
            onClick={() => {
              if (!isDemo) {
                onDelete(user.id);
              }
            }}
            disabled={isDemo}
            title={isDemo ? 'デモモードでは削除できません' : '削除'}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
              isDemo
                ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                : 'text-red-600 dark:text-white bg-red-50 dark:bg-red-600 hover:bg-red-100 dark:hover:bg-red-700 focus:ring-red-500 dark:focus:ring-offset-gray-800'
            }`}
          >
            <TrashIcon className='h-4 w-4 mr-2' />
            削除
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
